import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  FormSelect,
  FormInput,
  FormRadioButton,
  FormTextArea,
  FieldTable,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldFormattedValue,
  getFieldName,
  getFieldText,
  getFieldValue,
  getFieldDate,
  clearError,
  ArrayCheck,
  stripHtml,
} from "../../scripts/helperScripts";
import {
  getClientCarePlansPromise,
  getCarePlanDetails,
  getRecordedServicesPromise,
  getReferralsByClient,
  getFormsySubmissionsByFormIdForClient,
  getSoarCorrespondence,
  getProgressNotes,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function INEFMonthlyReport(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    const obj = { ...data };
    delete obj.ServiceDetails["@value"];
    delete obj.ServiceGoals["@value"];
    cloneForm(
      obj,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  const [recordedServices, setRecordedServices] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [clientPSDs, setClientPSDs] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [correspondences, setCorrespondences] = useState([]);
  const [progressNotes, setProgressNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const reportStartDate = useMemo(() => {
    if (!data.StartDate?.["@value"]) {
      return;
    }

    const startDate = moment(data.StartDate["@value"]);

    if (startDate.year() < 2000) {
      return;
    }

    return startDate;
  }, [data]);

  const reportEndDate = useMemo(() => {
    if (!data.EndDate?.["@value"]) {
      return;
    }

    const endDate = moment(data.EndDate["@value"]);

    if (endDate.year() < 2000) {
      return;
    }

    return endDate;
  }, [data]);

  const loadAllRecordsCallback = useCallback(async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    const response = await Promise.all([
      getRecordedServicesPromise(clientid),
      getReferralsByClient(clientid),
      getFormsySubmissionsByFormIdForClient(
        "EE5E6CD7-4BC6-EC11-997E-0050F21C653C", // IN EF PSD Form Container Id
        clientid
      ),
      getClientCarePlansPromise(clientid),
      getSoarCorrespondence(clientid),
      getProgressNotes(clientid),
    ]);

    console.log("LoadingResults:", response);
    if (!isMountedRef.current || !response) {
      return;
    }

    const recordedServiceResults = response[0];
    const referralResults = response[1];
    const psdResults = response[2];
    const carePlanResults = response[3];
    const correspondenceResults = response[4];
    const progressNotesResults = response[5];

    if (recordedServiceResults?.length > 0) {
      const activeSignedServices = recordedServiceResults.filter((service) => {
        const serviceItem = getFieldName(service.ccx_serviceitem);

        return (
          getFieldName(service.ccx_esignature) === "Yes" &&
          serviceItem !== "Daily Attendance" &&
          serviceItem !== "Drive Time"
        );
      });

      setRecordedServices(activeSignedServices);
    }

    if (referralResults?.length > 0) {
      const acceptedReferrals = referralResults.filter(
        (referral) => referral.Status === "Accepted"
      );
      setReferrals(acceptedReferrals);
    }

    if (psdResults) {
      const parsedPSD = JSON.parse(psdResults);

      if (
        parsedPSD &&
        parsedPSD.FormSubmissions &&
        parsedPSD.FormSubmissions.Submission &&
        parsedPSD.FormSubmissions.Submission.length > 0
      ) {
        const finalSignedPSDs = parsedPSD.FormSubmissions.Submission.filter(
          (psd) => psd.FinalSigned === "1"
        );

        setClientPSDs(finalSignedPSDs);
      }
    }

    if (carePlanResults?.length > 0) {
      const nonDraftCarePlans = carePlanResults.filter(
        (carePlan) =>
          !getFieldName(carePlan.ccx_careplan_status).includes("Draft")
      );

      setCarePlans(nonDraftCarePlans);
    }

    if (correspondenceResults?.length > 0) {
      setCorrespondences(correspondenceResults);
    }

    if (progressNotesResults?.length > 0) {
      setProgressNotes(progressNotesResults);
    }

    setLoaded(true);
    setLoading(false);
  }, [clientid, loading]);

  const convertRecordedServices = (
    obj,
    selectedProgram,
    startDate,
    endDate
  ) => {
    console.log("Recorded Services", recordedServices);
    const monthlyRecordedServices = recordedServices.filter((service) => {
      const serviceStartTime = moment(getFieldDate(service.ccx_starttime));

      return (
        getFieldText(service.ccx_clientprogram) === selectedProgram &&
        serviceStartTime.isSameOrAfter(startDate) &&
        serviceStartTime.isSameOrBefore(endDate)
      );
    });
    console.log("Monthly Recorded Services", monthlyRecordedServices);

    if (monthlyRecordedServices.length > 0) {
      const existingServiceDetails =
        obj.ServiceDetails !== undefined &&
        obj.ServiceDetails["@value"] !== undefined
          ? JSON.parse(obj.ServiceDetails["@value"])
          : [];
      console.log("Existing Service Details", existingServiceDetails);

      const existingRecordIds = existingServiceDetails.map(
        (service) => service.RecordId
      );
      console.log("Existing Record Ids", existingRecordIds);

      let serviceProviders = monthlyRecordedServices
        .map((service) => service.ccx_provider)
        .filter((value, index, self) => self.indexOf(value) === index)
        .join("; ");

      const existingServiceProviders = obj.ServiceProviders["@value"];

      if (
        existingServiceProviders !== undefined &&
        existingServiceProviders !== ""
      )
        serviceProviders = [serviceProviders, existingServiceProviders]
          .filter(
            (value, index, self) =>
              value !== "" && self.indexOf(value) === index
          )
          .join("; ");
      console.log("Service Providers", serviceProviders);

      const monthlyServiceDetails = monthlyRecordedServices
        .filter(
          (service) =>
            !existingRecordIds.includes(service.ccx_recorded_serviceid)
        )
        .map((service) => {
          const notes = progressNotes
            .filter(
              (note) =>
                getFieldText(note.ccx_recordedservice) ===
                service.ccx_recorded_serviceid
            )
            .sort((a, b) => {
              const aDate = moment(getFieldText(a.createdon));
              const bDate = moment(getFieldText(b.createdon));

              return aDate.isAfter(bDate) ? 1 : aDate.isBefore(bDate) ? -1 : 0;
            });

          const serviceStartTime = moment(getFieldText(service.ccx_starttime));
          const serviceEndTime = moment(getFieldText(service.ccx_endtime));

          return {
            IsEditable: true,
            RecordId: service.ccx_recorded_serviceid,
            ContactDate: serviceStartTime.format("M/D/YYYY"),
            StartTime: serviceStartTime.format("h:mm A"),
            EndTime: serviceEndTime.format("h:mm A"),
            Duration: getFieldFormattedValue(service.ccx_minutes),
            Method: getFieldName(service.ccx_deliverymethod),
            Location: getFieldName(service.ccx_placeofservice),
            Present: getFieldName(service.ccx_caregiver),
            FullStartDate: serviceStartTime,
            FullEndDate: serviceEndTime,
            ActivityDescription: notes[0]?.ccx_note
              ? stripHtml(notes[0].ccx_note)
              : null,
          };
        });
      console.log("Service Details", monthlyServiceDetails);

      const allServiceDetails = existingServiceDetails.concat(
        monthlyServiceDetails
      );

      allServiceDetails.sort((a, b) => {
        const aStart = moment(a.FullStartDate);
        const bStart = moment(b.FullStartDate);

        return aStart.isBefore(bStart) ? -1 : bStart.isBefore(aStart) ? 1 : 0;
      });
      console.log("All Service Details", allServiceDetails);

      const cancelledByFamily = monthlyRecordedServices.filter(
        (service) =>
          getFieldText(service.ccx_servicestatuslookup) ===
          "{9951503A-59E9-E211-90BC-005056822045}"
      );
      console.log("Cancelled by Family", cancelledByFamily);

      const cancelledByProvider = monthlyRecordedServices.filter(
        (service) =>
          getFieldText(service.ccx_servicestatuslookup) ===
          "{996BDD27-59E9-E211-90BC-005056822045}"
      );
      console.log("Cancelled by Provider", cancelledByProvider);

      const noShows = monthlyRecordedServices.filter(
        (service) =>
          getFieldText(service.ccx_servicestatuslookup) ===
          "{A4900888-00C0-E211-90BC-005056822045}"
      );
      console.log("No Shows", noShows);

      obj.ServiceProviders["@value"] = serviceProviders;
      obj.ServiceDetails["@value"] = JSON.stringify(allServiceDetails);
      obj.CancelledByFamily["@value"] = `${cancelledByFamily.length}`;
      obj.CancelledByProvider["@value"] = `${cancelledByProvider.length}`;
      obj.NoShows["@value"] = `${noShows.length}`;
      setData(obj);
    }
  };

  const convertReferrals = (obj, selectedProgram) => {
    console.log("Referrals", referrals);
    const referral = referrals.filter(
      (referral) => `{${referral.SOARClientProgramId}}` === selectedProgram
    )[0];

    console.log("Referral", referral);
    if (referral) {
      obj.ReferralAgency["@value"] = referral.ReferringAgencyName;
      obj.ReferralDate["@value"] = moment(referral.DateofReferral).format(
        moment.HTML5_FMT.DATE
      );
      obj.ReferralAcceptDate["@value"] = moment(
        referral.StatusChangeDate
      ).format(moment.HTML5_FMT.DATE);
      setData(obj);
    }
  };

  const convertPSDs = (obj, selectedProgram, endDate) => {
    if (!obj.ReferralReason["@value"] && !obj.FamilyStrengths["@value"]) {
      console.log("PSDs", clientPSDs);
      const earliestPSD = clientPSDs
        .filter((psd) => {
          const dateCompleted = moment(
            psd.FormResults.results.DateCompleted["@value"]
          );

          return (
            `{${psd.ClientProgramId}}` === selectedProgram &&
            dateCompleted.isSameOrBefore(endDate)
          );
        })
        .sort((a, b) => {
          const aDate = moment(a.FormResults.results.DateCompleted["@value"]);
          const bDate = moment(b.FormResults.results.DateCompleted["@value"]);

          return aDate.isBefore(bDate) ? 1 : bDate.isBefore(aDate) ? -1 : 0;
        })[0];

      console.log("Earliest PSD", earliestPSD);
      if (earliestPSD) {
        const psdResults = earliestPSD.FormResults.results;
        obj.ReferralReason["@value"] = psdResults.ReferralReason["@value"];
        obj.FamilyStrengths["@value"] = psdResults.ProtectiveFactors["@value"];
        setData(obj);

        if (
          errors.ReferralReason === undefined &&
          errors.FamilyStrengths === undefined
        ) {
          return;
        }

        clearError(errors, setErrors, "ReferralReason");
        clearError(errors, setErrors, "FamilyStrengths");
      }
    }
  };

  const convertCarePlans = async (obj, selectedProgram, endDate) => {
    console.log("Care Plans", carePlans);
    const carePlanForMonth = carePlans
      .filter((carePlan) => {
        const effectiveDate = moment(getFieldDate(carePlan.ccx_effectivedate));

        return (
          getFieldText(carePlan.ccx_clientprogram) === selectedProgram &&
          effectiveDate.isSameOrBefore(endDate)
        );
      })
      .sort((a, b) => {
        const aStart = moment(getFieldDate(a.ccx_effectivedate));
        const bStart = moment(getFieldDate(b.ccx_effectivedate));

        return aStart.isBefore(bStart) ? 1 : bStart.isBefore(aStart) ? -1 : 0;
      })[0];

    console.log("Monthly Care Plan", carePlanForMonth);
    if (carePlanForMonth) {
      const response = await getCarePlanDetails(
        carePlanForMonth.ccx_careplanid
      );
      if (!isMountedRef.current || !response || !response.plan_goals) {
        return;
      }

      const existingGoals =
        obj.ServiceGoals !== undefined &&
        obj.ServiceGoals["@value"] &&
        obj.ServiceGoals["@value"] !== ""
          ? JSON.parse(obj.ServiceGoals["@value"])
          : [];

      const existingRecordIds = existingGoals.map((goal) => goal.RecordId);

      const serviceGoals = response.plan_goals
        .filter((goal) => !existingRecordIds.includes(goal.ccx_GoalId))
        .map((goal) => {
          return {
            IsEditable: true,
            RecordId: goal.ccx_GoalId,
            Goal: goal.ccx_Description,
          };
        });

      const allGoals = existingGoals.concat(serviceGoals);

      if (allGoals.length > 0) {
        obj.ServiceGoals["@value"] = JSON.stringify(allGoals);
      } else {
        obj.ServiceGoals["@value"] = "";
      }

      setData(obj);
    }
  };

  const convertCorrespondence = (obj, startDate, endDate) => {
    console.log("SOAR Correspondence", correspondences);
    const monthlyCorrespondence = correspondences.filter((correspondence) => {
      const correspondenceStartTime = moment(
        getFieldDate(correspondence.sor_dateandstarttime)
      );

      return (
        correspondenceStartTime.isSameOrAfter(startDate) &&
        correspondenceStartTime.isSameOrBefore(endDate)
      );
    });
    console.log("Monthly SOAR Correspondence", monthlyCorrespondence);

    if (monthlyCorrespondence.length > 0) {
      const existingServiceDetails =
        obj.ServiceDetails !== undefined &&
        obj.ServiceDetails["@value"] !== undefined
          ? JSON.parse(obj.ServiceDetails["@value"])
          : [];
      console.log("Existing Service Details", existingServiceDetails);

      const existingRecordIds = existingServiceDetails.map(
        (service) => service.RecordId
      );
      console.log("Existing Record Ids", existingRecordIds);

      const monthlyServiceDetails = monthlyCorrespondence
        .filter(
          (correspondence) =>
            !existingRecordIds.includes(correspondence.sor_soarcorrespondanceid)
        )
        .map((correspondence) => {
          const correspondenceStartTime = moment(
            getFieldText(correspondence.sor_dateandstarttime)
          );
          const correspondenceEndTime = moment(
            getFieldText(correspondence.sor_dateandendtime)
          );

          return {
            IsEditable: true,
            RecordId: correspondence.sor_soarcorrespondanceid,
            ContactDate: correspondenceStartTime.format("M/D/YYYY"),
            StartTime: correspondenceStartTime.format("h:mm A"),
            EndTime: correspondenceEndTime.format("h:mm A"),
            Duration: getFieldFormattedValue(correspondence.sor_minutes),
            Method: getFieldName(correspondence.sor_deliverymethod),
            Location: getFieldName(correspondence.sor_placeofservice),
            Present: "" /* it seems like we don't capture user or
                            caregiver info for correspondence */,
            FullStartDate: correspondenceStartTime,
            FullEndDate: correspondenceEndTime,
            ActivityDescription: correspondence.sor_progressnote,
          };
        });
      console.log("Service Details", monthlyServiceDetails);

      const allServiceDetails = existingServiceDetails.concat(
        monthlyServiceDetails
      );

      allServiceDetails.sort((a, b) => {
        const aStart = moment(a.FullStartDate);
        const bStart = moment(b.FullStartDate);

        return aStart.isBefore(bStart) ? -1 : bStart.isBefore(aStart) ? 1 : 0;
      });
      console.log("All Service Details", allServiceDetails);

      const cancelledByFamily = monthlyCorrespondence.filter(
        (correspondence) =>
          getFieldText(correspondence.ccx_servicestatus) === "186460001"
      );
      console.log("Cancelled by Family", cancelledByFamily);

      const cancelledByProvider = monthlyCorrespondence.filter(
        (correspondence) =>
          getFieldText(correspondence.ccx_servicestatus) === "186460002"
      );
      console.log("Cancelled by Provider", cancelledByProvider);

      const noShows = monthlyCorrespondence.filter(
        (correspondence) =>
          getFieldText(correspondence.ccx_servicestatus) === "186460003"
      );
      console.log("No Shows", noShows);

      obj.ServiceDetails["@value"] = JSON.stringify(allServiceDetails);
      obj.CancelledByFamily["@value"] = `${cancelledByFamily.length}`;
      obj.CancelledByProvider["@value"] = `${cancelledByProvider.length}`;
      obj.NoShows["@value"] = `${noShows.length}`;

      setData(obj);
    }
  };

  const convertAllRecords = (obj = { ...data }) => {
    let selectedProgram = obj?.SelectedProgram["@value"] || selectedProgramId;
    let startDate = obj?.StartDate["@value"]
      ? moment(obj.StartDate["@value"])
      : reportStartDate;
    let endDate = obj?.EndDate["@value"]
      ? moment(obj.EndDate["@value"])
      : reportEndDate;

    if (!selectedProgram || !startDate || !endDate) {
      return;
    }

    convertRecordedServices(obj, selectedProgram, startDate, endDate);
    convertReferrals(obj, selectedProgram);
    convertPSDs(obj, selectedProgram, endDate);
    convertCarePlans(obj, selectedProgram, endDate);
    convertCorrespondence(obj, startDate, endDate);
  };

  const recommendationOptions = [
    { _id: "Continue", name: "Continue" },
    { _id: "End", name: "End" },
  ];

  const referralAgencyOptions = [
    { _id: "Allen", name: "Allen" },
    { _id: "Benton", name: "Benton" },
    { _id: "Clark", name: "Clark" },
    { _id: "Dearborn", name: "Dearborn" },
    { _id: "Decatur", name: "Decatur" },
    { _id: "DeKalb", name: "DeKalb" },
    { _id: "Elkhart", name: "Elkhart" },
    { _id: "Floyd", name: "Floyd" },
    { _id: "Harrison", name: "Harrison" },
    { _id: "Jasper", name: "Jasper" },
    { _id: "Jefferson", name: "Jefferson" },
    { _id: "Kosciusko", name: "Kosciusko" },
    { _id: "LaGrange", name: "LaGrange" },
    { _id: "Lake", name: "Lake" },
    { _id: "LaPorte", name: "LaPorte" },
    { _id: "Marion", name: "Marion" },
    { _id: "Marshall", name: "Marshall" },
    { _id: "Newton", name: "Newton" },
    { _id: "Noble", name: "Noble" },
    { _id: "Ohio", name: "Ohio" },
    { _id: "Porter", name: "Porter" },
    { _id: "Pulaski", name: "Pulaski" },
    { _id: "Ripley", name: "Ripley" },
    { _id: "Scott", name: "Scott" },
    { _id: "St. Joseph", name: "St. Joseph" },
    { _id: "Starke", name: "Starke" },
    { _id: "Steuben", name: "Steuben" },
    { _id: "Switzerland", name: "Switzerland" },
    { _id: "Washington", name: "Washington" },
    { _id: "Whitley", name: "Whitley" },
    { _id: "Other", name: "Other" },
  ];

  useEffect(() => {
    if (!formDisabled && !loading && !loaded) {
      loadAllRecordsCallback();
    }
  }, [formDisabled, loading, loaded, loadAllRecordsCallback]);

  useEffect(() => {
    if (
      selectedProgramId === "" ||
      clientData.programs === undefined ||
      clientData.programs.length === 0
    ) {
      return;
    }

    const selectedClientProgram = clientData.programs.filter(
      (option) => option.ccx_client_programid === selectedProgramId
    )[0];

    if (
      !selectedClientProgram ||
      !data.DeliveredServiceUnits ||
      data.DeliveredServiceUnits["@value"]
    ) {
      return;
    }

    const admitDate = moment(
      getFieldText(selectedClientProgram.ccx_admitdate)
    ).startOf("day");
    const today = moment().startOf("day");
    const obj = { ...data };
    obj.DeliveredServiceUnits["@value"] = today
      .diff(admitDate, "days")
      .toString();
    setData(obj);

    if (
      formsyDetails.ClientProgramId ===
      selectedClientProgram.ccx_client_programid
    ) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = selectedClientProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(
      selectedClientProgram.ccx_programunit
    );
    setFormsyDetails(details);
  }, [selectedProgramId, clientData.programs, data, formsyDetails]);

  const handleReportDateChange = (e) => {
    const obj = handleChange(e);

    if (
      !obj.SelectedProgram["@value"] ||
      !obj.StartDate["@value"] ||
      !obj.EndDate["@value"]
    ) {
      return;
    }

    const startDate = moment(obj.StartDate["@value"]);
    const endDate = moment(obj.EndDate["@value"]);
    const lastDayOfMonth = moment(obj.StartDate["@value"])
      .add(1, "month")
      .subtract(1, "day")
      .date();

    if (
      startDate.year() !== endDate.year() ||
      startDate.month() !== endDate.month() ||
      startDate.date() !== 1 ||
      endDate.date() !== lastDayOfMonth
    ) {
      return;
    }

    convertAllRecords(obj);
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="StartDate"
                    label="Start Date"
                    error={errors.StartDate}
                    value={data.StartDate["@value"]}
                    onChange={handleReportDateChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loaded}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="EndDate"
                    label="End Date"
                    error={errors.EndDate}
                    value={data.EndDate["@value"]}
                    onChange={handleReportDateChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loaded}
                    type="date"
                  />
                </div>
                {!formDisabled ? (
                  <div className="col-1 pt-3">
                    <i
                      title="Refresh All Records"
                      className={`fas fa-sync fa-lg mt-4${
                        reportStartDate === "" ||
                        reportEndDate === "" ||
                        selectedProgramId === ""
                          ? " text-secondary"
                          : " text-body"
                      }`}
                      onClick={() => {
                        if (
                          reportStartDate !== "" &&
                          reportEndDate !== "" &&
                          selectedProgramId !== ""
                        ) {
                          const refreshRecords = async () => {
                            await loadAllRecordsCallback();
                            convertAllRecords();
                          };

                          refreshRecords();
                        }
                      }}
                    ></i>
                  </div>
                ) : null}
              </div>
              <div className="row">
                <div className="col">Parent(s) Name:</div>
                <div className="col">
                  <FormInput
                    name="ParentName"
                    error={errors.ParentName}
                    value={data.ParentName["@value"]}
                    onChange={handleChange}
                    identical={compare("ParentName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">Child(ren):</div>
                <div className="col">
                  <FormInput
                    name="ChildName"
                    error={errors.ChildName}
                    value={data.ChildName["@value"]}
                    onChange={handleChange}
                    identical={compare("ChildName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">Referral Agency:</div>
                <div className="col">
                  <FormSelect
                    name="ReferralAgency"
                    error={errors.ReferralAgency}
                    value={data.ReferralAgency["@value"]}
                    options={referralAgencyOptions}
                    onChange={handleChange}
                    identical={compare("ReferralAgency")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.ReferralAgency["@value"] === "Other" && (
                    <FormInput
                      name="ReferralAgencyDetails"
                      error={errors.ReferralAgencyDetails}
                      value={data.ReferralAgencyDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("ReferralAgencyDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col">Case Manager/Probation Officer:</div>
                <div className="col">
                  <FormInput
                    name="CaseManager"
                    error={errors.CaseManager}
                    value={data.CaseManager["@value"]}
                    onChange={handleChange}
                    identical={compare("CaseManager")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col py-2">Service Standard:</div>
                <div className="col py-2">Provider Agency Staff:</div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="ServiceStandard"
                    error={errors.ServiceStandard}
                    value={data.ServiceStandard["@value"]}
                    onChange={handleChange}
                    identical={compare("ServiceStandard")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="ServiceProviders"
                    error={errors.ServiceProviders}
                    value={data.ServiceProviders["@value"]}
                    onChange={handleChange}
                    identical={compare("ServiceProviders")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col-1">
                  <i
                    className="fas fa-sync"
                    title="Refresh Services Provided"
                    onClick={() => {
                      if (
                        reportStartDate &&
                        reportEndDate &&
                        selectedProgramId
                      ) {
                        convertRecordedServices(
                          { ...data },
                          selectedProgramId,
                          reportStartDate,
                          reportEndDate
                        );
                      }
                    }}
                  ></i>
                </div>
              </div>
              <br />
              <br />
              <div className="container">
                <div className="row border">
                  <div className="col pt-2 pb-4 border-end">
                    Service Provided (Service Standard)
                  </div>
                  <div className="col pt-2">
                    {data.ServiceStandard["@value"] !== undefined
                      ? data.ServiceStandard["@value"]
                      : ""}
                  </div>
                </div>
                <div className="row border-start border-end border-bottom">
                  <div className="col pt-2 border-end">
                    Begin/End Date of Referral
                  </div>
                  <div className="col pt-2">
                    <FormInput
                      name="ReferralDate"
                      error={errors.ReferralDate}
                      value={data.ReferralDate["@value"]}
                      onChange={handleChange}
                      identical={compare("ReferralDate")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start border-end border-bottom">
                  <div className="col pt-2 border-end">
                    Service Provider Staff
                  </div>
                  <div className="col pt-2">
                    <FormInput
                      name="ServiceProviders"
                      error={errors.ServiceProviders}
                      value={data.ServiceProviders["@value"]}
                      onChange={handleChange}
                      identical={compare("ServiceProviders")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start border-end border-bottom">
                  <div className="col pt-2 border-end">
                    Number of Service Unit Authorized
                  </div>
                  <div className="col pt-2">
                    <FormInput
                      name="AuthorizedServiceUnits"
                      error={errors.AuthorizedServiceUnits}
                      value={data.AuthorizedServiceUnits["@value"]}
                      onChange={handleChange}
                      identical={compare("AuthorizedServiceUnits")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start border-end border-bottom">
                  <div className="col pt-2 border-end">
                    Number of Service Units delivered to end of report period
                  </div>
                  <div className="col pt-2">
                    <FormInput
                      name="DeliveredServiceUnits"
                      error={errors.DeliveredServiceUnits}
                      value={data.DeliveredServiceUnits["@value"]}
                      onChange={handleChange}
                      identical={compare("DeliveredServiceUnits")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              {data.ServiceDetails["@value"] !== undefined ? (
                <FieldTable
                  name="ServiceDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col-2 border-end py-2">Contact Date</div>
                      <div className="col border-end py-2">Start Time</div>
                      <div className="col border-end py-2">End Time</div>
                      <div className="col border-end py-2">Duration</div>
                      <div className="col-2 border-end py-2">Method</div>
                      <div className="col-2 border-end py-2">Location</div>
                      <div className="col-3 py-2">Those Present</div>
                    </div>
                    {data.ServiceDetails["@value"]
                      ? JSON.parse(data.ServiceDetails["@value"]).map(
                          (item, index) => (
                            <React.Fragment key={item._id}>
                              <div className="row border-bottom">
                                <div className="col-2 border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            setData,
                                            index,
                                            "ServiceDetails"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "ServiceDetails"
                                          );
                                        }}
                                      ></i>{" "}
                                      {moment.isDate(item.ContactDate)
                                        ? moment(item.ContactDate).format(
                                            "M/D/YYYY"
                                          )
                                        : item.ContactDate}
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "ServiceDetails"
                                          );
                                        }}
                                      ></i>
                                      {moment.isDate(item.ContactDate)
                                        ? moment(item.ContactDate).format(
                                            "M/D/YYYY"
                                          )
                                        : item.ContactDate}
                                    </>
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.StartTime}
                                </div>
                                <div className="col border-end py-2">
                                  {item.EndTime}
                                </div>
                                <div className="col border-end py-2">
                                  {item.Duration}
                                </div>
                                <div className="col-2 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Method"
                                      value={item.Method}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "ServiceDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Method
                                  )}
                                </div>
                                <div className="col-2 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Location"
                                      value={item.Location}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "ServiceDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Location
                                  )}
                                </div>
                                <div className="col-3 py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Present"
                                      value={item.Present}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "ServiceDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Present
                                  )}
                                </div>
                              </div>
                              <div className="row border-bottom">
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="ActivityDescription"
                                      label="Activity Description:"
                                      value={item.ActivityDescription}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "ServiceDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.ActivityDescription
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          )
                        )
                      : null}
                  </div>
                  {errors.ServiceDetails !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.ServiceDetails}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
              ) : null}
              <FormInput
                name="ReferralAcceptDate"
                label="Referral accepted on"
                error={errors.ReferralAcceptDate}
                value={data.ReferralAcceptDate["@value"]}
                onChange={handleChange}
                identical={compare("ReferralAcceptDate")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="PhoneContact"
                label="Initial phone/text contact on"
                error={errors.PhoneContact}
                value={data.PhoneContact["@value"]}
                onChange={handleChange}
                identical={compare("PhoneContact")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="FaceToFaceContact"
                label="Initial face-to-face service contact on"
                error={errors.FaceToFaceContact}
                value={data.FaceToFaceContact["@value"]}
                onChange={handleChange}
                identical={compare("FaceToFaceContact")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="ReferralReason"
                label="Reason for Referral and Presenting Issues:"
                error={errors.ReferralReason}
                value={data.ReferralReason["@value"]}
                onChange={handleChange}
                identical={compare("ReferralReason")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="FamilyStrengths"
                label="Family Functional Strengths:"
                error={errors.FamilyStrengths}
                value={data.FamilyStrengths["@value"]}
                onChange={handleChange}
                identical={compare("FamilyStrengths")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="Recommendations"
                label="Overall recommendation and progress summary:"
                error={errors.Recommendations}
                value={data.Recommendations["@value"]}
                onChange={handleChange}
                identical={compare("Recommendations")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  Number of Appointments cancelled by Family:
                </div>
                <div className="col">
                  <FormInput
                    name="CancelledByFamily"
                    error={errors.CancelledByFamily}
                    value={data.CancelledByFamily["@value"]}
                    onChange={handleChange}
                    identical={compare("CancelledByFamily")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  Number of Appointments cancelled by Provider:
                </div>
                <div className="col">
                  <FormInput
                    name="CancelledByProvider"
                    error={errors.CancelledByProvider}
                    value={data.CancelledByProvider["@value"]}
                    onChange={handleChange}
                    identical={compare("CancelledByProvider")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">No Shows:</div>
                <div className="col">
                  <FormInput
                    name="NoShows"
                    error={errors.NoShows}
                    value={data.NoShows["@value"]}
                    onChange={handleChange}
                    identical={compare("NoShows")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <br />
              <br />
              {data.ServiceGoals["@value"] ? (
                <FieldTable
                  name="ServiceGoals"
                  commentsHandler={handleComments}
                >
                  {JSON.parse(data.ServiceGoals["@value"]).map(
                    (item, index) => (
                      <div
                        key={`ServiceGoals-${index}`}
                        className="border-start border-end border-top mb-3"
                      >
                        <div className="p-2 border-bottom">
                          <FormInput
                            name="Goal"
                            label={
                              <>
                                <i
                                  className="far fa-trash-alt me-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteRow(index, "ServiceGoals");
                                  }}
                                ></i>{" "}
                                DCS Service Goal:
                              </>
                            }
                            value={item.Goal}
                            onChange={(e) => {
                              handleRowChange(e, index, "ServiceGoals");
                            }}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="p-2 border-bottom">
                          <FormTextArea
                            name="Narrative"
                            label="Narrative Discussion of Services provided for this goal during month:"
                            value={item.Narrative}
                            onChange={(e) => {
                              handleRowChange(e, index, "ServiceGoals");
                            }}
                            disabled={formDisabled}
                            className="border-bottom"
                          />
                        </div>
                        <div className="p-2 border-bottom">
                          <FormInput
                            name="ProgressSummary"
                            label="Progress Summary toward goal:"
                            value={item.ProgressSummary}
                            onChange={(e) => {
                              handleRowChange(e, index, "ServiceGoals");
                            }}
                            disabled={formDisabled}
                            className="border-bottom"
                          />
                        </div>
                        <div className="p-2 border-bottom">
                          <FormInput
                            name="Cooperativeness"
                            label="Family cooperativeness:"
                            value={item.Cooperativeness}
                            onChange={(e) => {
                              handleRowChange(e, index, "ServiceGoals");
                            }}
                            disabled={formDisabled}
                            className="border-bottom"
                          />
                        </div>
                        <div className="p-2 border-bottom">
                          <FormRadioButton
                            name={`Recommendation-${index}`}
                            label="Recommendation regarding services for goal:"
                            value={item.Recommendation}
                            options={recommendationOptions}
                            onChange={(e) => {
                              const obj = {
                                currentTarget: {
                                  name: e.currentTarget.name.split("-")[0],
                                  value: e.currentTarget.value,
                                  type: "radio",
                                },
                              };
                              handleRowChange(obj, index, "ServiceGoals");
                            }}
                            disabled={formDisabled}
                            className="border-bottom"
                          />
                        </div>
                        {item.Recommendation === "Continue" ? (
                          <div className="p-2 border-bottom">
                            <FormInput
                              name="Reason"
                              label="Reason:"
                              value={item.Reason}
                              onChange={(e) => {
                                handleRowChange(e, index, "ServiceGoals");
                              }}
                              disabled={formDisabled}
                              className="border-bottom"
                            />
                          </div>
                        ) : null}
                      </div>
                    )
                  )}
                  {errors.ServiceGoals !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.ServiceGoals}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
              ) : (
                <FormInput
                  name="ServiceGoalsNarrative"
                  label="Goals:"
                  error={errors.ServiceGoalsNarrative}
                  value={data.ServiceGoalsNarrative?.["@value"]}
                  onChange={handleChange}
                  identical={compare("ServiceGoalsNarrative")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default INEFMonthlyReport;
